import { Component, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { InventoryPeriodModel } from 'src/app/models/inventory/inventory-period.model';
import { InventoryPeriodService } from 'src/app/services/inventory/inventory-period.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-inventory-period',
  templateUrl: './register-inventory-period.component.html',
  styleUrls: ['./register-inventory-period.component.scss'],
  standalone: false
})
export class RegisterInventoryPeriodComponent implements OnInit {

  formInventoryPeriod: FormGroup = new FormGroup({});
  inventoryPeriodContract: InventoryPeriodModel = new InventoryPeriodModel();
  permissionUpdatable: boolean = false;
  subscription: Subscription;

  constructor(public config: DynamicDialogConfig,
    private formBuilder: FormBuilder,
    private toastHandlerService: ToastHandlerService,
    private inventoryPeriodService: InventoryPeriodService,
    @Optional() private ref: DynamicDialogRef) {
    this.validateForm(new InventoryPeriodModel());
  }

  async ngOnInit() {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.subscription = input$?.subscribe((inputs: any) => {
      this.inventoryPeriodContract = inputs.inventoryPeriodContract;
      this.permissionUpdatable = inputs.permissionUpdatable;
    });
    this.createNewRegister();
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  createNewRegister() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast('warning', `El usuario no tiene permisos para crear períodos de inventario.
        Comuníquese con el administrador del sistema.`);
      return;
    }
    this.validateForm(this.inventoryPeriodContract);
    this.setYear();
  }

  close() {
    this.inventoryPeriodContract = new InventoryPeriodModel();
    this.validateForm(this.inventoryPeriodContract);
    this.ref.close();
  }

  onReloadEmit() {
    const output$ = this.config.data?.output$;
    output$?.next({ type: 'onReload', payload: true });
  }

  setYear() {
    const currentYear = new Date().getFullYear();
    this.formInventoryPeriod.get('year')?.setValue(currentYear);
  }

  async send() {
    this.validateFormControls();
    if (this.formInventoryPeriod.valid) {
      if (!this.permissionUpdatable) {
        this.toastHandlerService.showToast('error', `El usuario no tiene permisos para crear períodos de inventario.
          Comuníquese con el administrador del sistema.`);
        return;
      }
      const toastId = 'InventoryPeriod';
      const modelInventoryPeriod = this.formInventoryPeriod.value as InventoryPeriodModel;
      try {
        this.toastHandlerService.showToast('info', 'Se crearán los saldos iniciales; este proceso puede tardar un poco.',
          'Información', toastId, 0);
        await firstValueFrom(this.inventoryPeriodService.createInventoryPeriod(modelInventoryPeriod));
        this.toastHandlerService.clearToastById(toastId);
        this.onReloadEmit();
        this.toastHandlerService.showToast('success', 'El período de inventario fue creado exitosamente.');
      } catch (error) {
        this.toastHandlerService.clearToastById(toastId);
        console.error('Error al crear el período de inventario.', error);
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      year: {
        required: 'El campo Año es obligatorio.',
        minlength: 'El campo Año debe tener al menos 4 dígitos.',
      },
      period: {
        required: 'El campo Período es obligatorio.',
        min: 'El período mínimo permitido es 1.',
        max: 'El período máximo permitido es 12.',
      },
    };

    Object.keys(controlMessages).forEach((controlName) => {
      const formControl = this.formInventoryPeriod.get(controlName);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[controlName];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  validateForm(inventoryPeriod: InventoryPeriodModel) {
    this.formInventoryPeriod = this.formBuilder.group({
      year: [inventoryPeriod.year, [Validators.required, Validators.minLength(4)]],
      period: [inventoryPeriod.period, [Validators.required, Validators.min(1), Validators.max(12)]]
    });
  }

  validateFormControls() {
    Object.keys(this.formInventoryPeriod.controls).forEach((key) => {
      const control = this.formInventoryPeriod.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
